import React, { useEffect, useMemo, useState } from "react";
import { Constants, WeightPullWeightClasses } from "@eagerdog/constants";
import { WeightPullScoreDto } from "@eagerdog/interfaces";
import {
  calculateWeightPullPercent,
  formatWeightPullPercent,
} from "@eagerdog/scoring-core";

import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import StopWatch, { convertTimeToSeconds, convertTimeToMinutes } from "../StopWatch/StopWatch";

import "./WeightPullResults.scss";

interface IProps {
  result: any,
  onChange(result: WeightPullScoreDto): void
}

const resultOptions: IOption[] = [
  { id: Constants.score_result_type.qualified, value: "Qualified" },
  { id: Constants.score_result_type.not_qualified, value: "NQ" },
  { id: Constants.score_result_type.absent, value: "Absent" },
];

const inferResultType = (weightPull: any): IOption => {
  if (weightPull?.result_type) {
    const match = resultOptions.find((o) => o.id === weightPull.result_type);
    if (match) {
      return match;
    }
  }
  if (weightPull?.time || weightPull?.percent_pulled) {
    return resultOptions[0];
  }
  return resultOptions[0];
};

const WeightPullResults: React.FC<IProps> = (props) => {
  const weightClassOptions: IOption[] = WeightPullWeightClasses.map((c) => ({
    id: c.label,
    value: c.label,
  }));

  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [result, setResult] = useState<IOption>(inferResultType(props.result.weight_pull));
  const [excluded, setExcluded] = useState<boolean>(props.result.weight_pull?.excluded_from_placement || false);
  const [weightClass, setWeightClass] = useState<IOption>(
    props.result.weight_pull?.weight_class
      ? { id: props.result.weight_pull.weight_class, value: props.result.weight_pull.weight_class }
      : { id: Constants.none, value: Constants.none }
  );
  const [dogWeight, setDogWeight] = useState<string>(
    props.result.weight_pull?.dog_weight ? props.result.weight_pull.dog_weight.toString() : ""
  );
  const [highestWeight, setHighestWeight] = useState<string>(
    props.result.weight_pull?.highest_weight ? props.result.weight_pull.highest_weight.toString() : ""
  );
  const [time, setTime] = useState<string>(
    props.result.weight_pull?.time
      ? convertTimeToMinutes(props.result.weight_pull.time)
      : ""
  );

  const isQualified = result.id === Constants.score_result_type.qualified;
  const computedPercent = useMemo(
    () => calculateWeightPullPercent(Number(highestWeight) || undefined, Number(dogWeight) || undefined),
    [highestWeight, dogWeight]
  );

  useEffect(() => {
    setHasChanged(true);
  }, []);

  useEffect(() => {
    if (hasChanged) {
      const payload: WeightPullScoreDto = {
        result_type: result.id,
        weight_class: weightClass.id !== Constants.none ? weightClass.id : undefined,
        dog_weight: Number(dogWeight) || undefined,
        weighed_by: props.result.weight_pull?.weighed_by,
        excluded_from_placement: excluded,
      };

      if (isQualified) {
        const parsedHighest = parseFloat(highestWeight);
        payload.highest_weight = Number.isFinite(parsedHighest) ? parsedHighest : undefined;
        payload.percent_pulled = computedPercent;
        payload.time = convertTimeToSeconds(time);
      }

      props.onChange(payload);
      setHasChanged(false);
    }
  }, [
    hasChanged,
    props,
    result.id,
    weightClass,
    dogWeight,
    highestWeight,
    time,
    excluded,
    isQualified,
    computedPercent,
  ]);

  return (
    <div className="WeightPullResults">
      <div className="row">
        <Dropdown
          value={result}
          label="Result"
          onChange={(_e: any, value: IOption) => { setResult(value); setHasChanged(true); }}
          options={resultOptions}
          placeholder=""
        />
      </div>
      {isQualified && (
        <>
          <div className="row">
            <StopWatch
              label="Time"
              hideMinutes
              time={time}
              onChange={(value: string) => { setTime(value); setHasChanged(true); }}
            />
          </div>
          <div className="row">
            <Dropdown
              value={weightClass}
              onChange={(_e: any, value: IOption) => { setWeightClass(value); setHasChanged(true); }}
              label="Weight Class"
              options={weightClassOptions}
              placeholder=""
            />
          </div>
          <div className="row">
            <Input
              label="Dog Weight (lbs)"
              id="dogWeight"
              type="number"
              min="0"
              step="0.01"
              required
              onChange={(e) => { e.stopPropagation(); setDogWeight(e.target.value); setHasChanged(true); }}
              placeholder=""
              defaultValue={dogWeight}
            />
          </div>
          <div className="row">
            <Input
              label="Highest Weight (lbs)"
              id="highestWeight"
              type="text"
              inputMode="decimal"
              onChange={(e) => {
                e.stopPropagation();
                const next = e.target.value;
                if (next === "" || /^\d*\.?\d*$/.test(next)) {
                  setHighestWeight(next);
                  setHasChanged(true);
                }
              }}
              placeholder=""
              defaultValue={highestWeight}
            />
          </div>
          <div className="row">
            <Input
              label="Percent Pulled (%)"
              id="pctPulled"
              type="text"
              disabled
              placeholder=""
              defaultValue={formatWeightPullPercent(computedPercent)}
            />
          </div>
          <div className="row">
            <Checkbox
              id="excludedFromPlacement"
              onChange={() => { setExcluded(!excluded); setHasChanged(true); }}
              value={excluded}
              label="Exclude from placements"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default WeightPullResults;
